// Contrat de l'urbanisme et de l'aménagement (M18, lot L5-C2, D-225, D-255 à D-259). La demande est un dossier
// du moteur des démarches (M05) ; l'urbanisme ajoute la configuration explicite des autorités et des types de
// demandes (compétence décrite, démarche liée, services d'avis, inspection exigée), les plans et zones avec
// emprises PostGIS, la localisation et la zone de la demande, les avis datés des services, les inspections
// liées (ordres de mission M23) et le contrôle de la décision par l'autorité configurée. Aucun titre foncier,
// aucune compétence officielle présumée : chaque élément configuré porte sa source administrative.
// La réponse publique (portail) a son propre schéma strict : ni demande, ni demandeur, ni avis.
import { z } from 'zod';
import { schemaPolygoneGeoJson, type PolygoneGeoJson } from './marches-domaine-public';
import { schemaPosition } from './marches-structure';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();
const codeConfig = z.string().trim().regex(/^[a-z][a-z0-9_]{1,40}$/, 'Code en minuscules, chiffres et soulignés');

export const ETATS_PLAN_URBANISME = { projet: 'En projet', en_vigueur: 'En vigueur', abroge: 'Abrogé' } as const;
export type EtatPlanUrbanisme = keyof typeof ETATS_PLAN_URBANISME;

export const SENS_AVIS_URBANISME = { favorable: 'Favorable', favorable_reserves: 'Favorable avec réserves', defavorable: 'Défavorable' } as const;
export type SensAvisUrbanisme = keyof typeof SENS_AVIS_URBANISME;

export const ETATS_AVIS_URBANISME = { sollicite: 'Sollicité', rendu: 'Rendu' } as const;
export type EtatAvisUrbanisme = keyof typeof ETATS_AVIS_URBANISME;

/** Avancement d'une demande, déduit de l'état du dossier M05 (aucun état propre à l'urbanisme). */
export const AVANCEMENTS_DEMANDE = { en_cours: 'En cours d\'instruction', decidee: 'Décidée', terminee: 'Terminée' } as const;
export type AvancementDemande = keyof typeof AVANCEMENTS_DEMANDE;

// --- Configuration (urbanisme : administrer) ------------------------------------------------------------

export const schemaCreationAutoriteUrbanisme = z.strictObject({
  code: codeConfig,
  libelle: texte(160, 3),
  /** Rôle de la collectivité dont le titulaire décide (directement ou par délégation de ce titulaire). */
  role_code: z.string().trim().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de rôle attendu'),
  /** Source administrative de l'autorité (acte, texte) : rien n'est présumé par l'application. */
  reference_acte: texte(300, 3),
  fictif: z.boolean().default(false),
});
export type CreationAutoriteUrbanisme = z.infer<typeof schemaCreationAutoriteUrbanisme>;

export const schemaCreationTypeDemande = z.strictObject({
  code: codeConfig,
  libelle: texte(160, 3),
  /** Compétence de la collectivité, décrite par elle-même avec sa source (jamais un titre foncier). */
  competence: texte(500, 3),
  reference_acte: texte(300, 3),
  /** Démarche M05 publiée qui porte le dépôt, les pièces, le complément, la décision et le document. */
  demarche_id: uuid,
  autorite_id: uuid,
  services_avis: z.array(uuid).max(10).default([]),
  inspection_requise: z.boolean().default(false),
  /** Code du champ « localisation » du formulaire de la démarche qui place la demande sur la carte. */
  champ_localisation: z.string().trim().regex(/^[a-z][a-z0-9_]{0,62}$/).optional(),
  delai_avis_jours: z.number().int().min(1).max(90).default(15),
  fictif: z.boolean().default(false),
}).refine((t) => new Set(t.services_avis).size === t.services_avis.length, { message: 'Un service figure une seule fois', path: ['services_avis'] });
export type CreationTypeDemande = z.infer<typeof schemaCreationTypeDemande>;

export const schemaActivationTypeDemande = z.strictObject({ actif: z.boolean() });

export const schemaCreationPlanUrbanisme = z.strictObject({
  code: codeConfig,
  intitule: texte(200, 3),
  reference_acte: texte(300, 3),
  fictif: z.boolean().default(false),
});
export type CreationPlanUrbanisme = z.infer<typeof schemaCreationPlanUrbanisme>;

export const schemaEtatPlanUrbanisme = z
  .strictObject({ etat: z.enum(['projet', 'en_vigueur', 'abroge']), approuve_le: jour.optional() })
  .refine((p) => p.etat !== 'en_vigueur' || Boolean(p.approuve_le), { message: 'Date d\'approbation exigée pour un plan en vigueur', path: ['approuve_le'] });
export type ModificationEtatPlan = z.infer<typeof schemaEtatPlanUrbanisme>;

export const schemaCreationZoneUrbanisme = z.strictObject({
  plan_id: uuid,
  code: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,19}$/, 'Code court de zone attendu'),
  libelle: texte(160, 2),
  vocation: texte(300, 3),
  prescriptions: facultatif(2000),
  emprise: schemaPolygoneGeoJson,
  fictif: z.boolean().default(false),
});
export type CreationZoneUrbanisme = z.infer<typeof schemaCreationZoneUrbanisme>;

// --- Demandes -------------------------------------------------------------------------------------------

export const schemaLocalisationDemande = z.strictObject({ position: schemaPosition, zone_id: uuid.optional() });
export type LocalisationDemande = z.infer<typeof schemaLocalisationDemande>;

export const schemaSollicitationAvis = z.strictObject({ service_id: uuid, objet: facultatif(500), echeance: jour.optional() });
export type SollicitationAvis = z.infer<typeof schemaSollicitationAvis>;

export const schemaRenduAvis = z.strictObject({
  sens: z.enum(['favorable', 'favorable_reserves', 'defavorable']),
  observations: texte(2000, 3),
  /** Date de l'avis (au plus tôt la sollicitation, au plus tard aujourd'hui), contrôlée en base. */
  date_avis: jour,
});
export type RenduAvis = z.infer<typeof schemaRenduAvis>;

/** Inspection liée : un ordre M23 existant, ou un nouvel ordre émis par le service des contrôles sur le lieu de la demande. */
export const schemaInspectionDemande = z
  .strictObject({
    ordre_id: uuid.optional(),
    controleur_appartenance_id: uuid.optional(),
    formulaire_id: uuid.optional(),
    periode_debut: jour.optional(),
    periode_fin: jour.optional(),
    consignes: facultatif(2000),
    motif: texte(300, 3),
  })
  .refine((i) => Boolean(i.ordre_id) || Boolean(i.controleur_appartenance_id && i.formulaire_id && i.periode_debut && i.periode_fin), {
    message: 'Indiquer un ordre existant, ou le contrôleur, le formulaire et la période du nouvel ordre',
    path: ['ordre_id'],
  })
  .refine((i) => !i.periode_debut || !i.periode_fin || i.periode_fin >= i.periode_debut, { message: 'La fin suit le début', path: ['periode_fin'] });
export type InspectionDemande = z.infer<typeof schemaInspectionDemande>;

export const schemaFiltreDemandesUrbanisme = z.strictObject({
  avancement: z.enum(['en_cours', 'decidee', 'terminee']).optional(),
  type_id: uuid.optional(),
  zone_id: uuid.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(200),
});

// --- Réponses de l'administration ----------------------------------------------------------------------

export interface AutoriteUrbanisme {
  id: string;
  code: string;
  libelle: string;
  role_code: string;
  role_libelle: string | null;
  reference_acte: string;
  fictif: boolean;
}

export interface TypeDemandeUrbanisme {
  id: string;
  code: string;
  libelle: string;
  competence: string;
  reference_acte: string;
  demarche: { id: string; code: string; libelle: string; etat: string };
  autorite: { id: string; libelle: string; role_code: string };
  services_avis: { id: string; code: string; nom: string }[];
  inspection_requise: boolean;
  champ_localisation: string | null;
  delai_avis_jours: number;
  actif: boolean;
  fictif: boolean;
}

export interface ZoneUrbanisme {
  id: string;
  plan_id: string;
  code: string;
  libelle: string;
  vocation: string;
  prescriptions: string | null;
  emprise: PolygoneGeoJson;
  surface_ha: number;
  fictif: boolean;
}

export interface PlanUrbanisme {
  id: string;
  code: string;
  intitule: string;
  reference_acte: string;
  etat: EtatPlanUrbanisme;
  approuve_le: string | null;
  fictif: boolean;
  zones: ZoneUrbanisme[];
}

export interface AvisUrbanisme {
  id: string;
  service: { id: string; code: string; nom: string };
  objet: string | null;
  etat: EtatAvisUrbanisme;
  sollicite_le: string;
  echeance: string;
  sens: SensAvisUrbanisme | null;
  observations: string | null;
  date_avis: string | null;
  rendu_le: string | null;
  rendu_par: string | null;
  requis: boolean;
  dans_delai: boolean | null;
}

export interface InspectionLiee {
  id: string;
  motif: string;
  lie_le: string;
  ordre: { id: string; numero: string; etat: string; periode_debut: string; periode_fin: string; conclusion: string | null; constats: number };
}

export interface ElementHistoriqueUrbanisme {
  horodatage: string;
  source: 'dossier' | 'avis' | 'inspection' | 'localisation';
  libelle: string;
  auteur: string | null;
  motif: string | null;
}

export interface ResumeDemandeUrbanisme {
  id: string;
  dossier_id: string;
  numero: string | null;
  type: { id: string; code: string; libelle: string };
  demandeur: string | null;
  etat_dossier: string;
  avancement: AvancementDemande;
  resultat: string | null;
  zone: { id: string; code: string; libelle: string } | null;
  position: { longitude: number; latitude: number } | null;
  avis: { requis: number; rendus: number };
  inspections: number;
  enregistre_le: string | null;
  decide_le: string | null;
}

/** Conditions de la décision (D-258) : ce qui manque encore est énoncé, rien n'est déduit. */
export interface ConditionsDecision {
  avis_manquants: string[];
  inspection_manquante: boolean;
  autorite: { libelle: string; role_code: string };
  peut_decider: boolean;
}

export interface DemandeUrbanisme extends ResumeDemandeUrbanisme {
  type_detail: TypeDemandeUrbanisme;
  avis_detail: AvisUrbanisme[];
  inspections_detail: InspectionLiee[];
  conditions: ConditionsDecision;
  historique: ElementHistoriqueUrbanisme[];
}

// --- Réponse publique (portail, sans compte) -----------------------------------------------------------

export const schemaUrbanismePublic = z.strictObject({
  collectivite: z.strictObject({ code: z.string(), nom: z.string() }),
  types: z.array(
    z.strictObject({ code: z.string(), libelle: z.string(), competence: z.string(), demarche_code: z.string(), demarche_libelle: z.string(), fictif: z.boolean() }),
  ),
  zones: z.array(
    z.strictObject({
      code: z.string(),
      libelle: z.string(),
      vocation: z.string(),
      plan: z.string(),
      emprise: schemaPolygoneGeoJson,
      centre: schemaPosition,
      fictif: z.boolean(),
    }),
  ),
});
export type UrbanismePublic = z.infer<typeof schemaUrbanismePublic>;
